import type {
  SimulationResponseUnitInfoInterface,
  SimulationResultInterface,
} from 'store/simulation/types';
import type {
  UnitInfoBananaScreenInterface,
  UnitInfoCMScreenInterface,
  UnitInfoFreeFallScreenSFInterface,
  UnitInfoFreeFallScreenSSInterface,
  UnitInfoGenericScreenInterface,
  UnitInfoGrizzlyScreenSGInterface,
  UnitInfoKwataniScreenInterface,
  UnitInfoLMScreenInterface,
  UnitInfoMultislopeScreenInterface,
  UnitInfoSchenckScreenInterface,
  UnitInterface,
} from '../../store/units/types';
import {
  bananaScreenDeckConfig,
  freeFallScreenDeckConfig,
  genericScreenDeckConfig,
  grizzlyScreenDeckConfig,
  grizzlyScreenDeckNames,
  kwataniScreenDeckConfig,
} from './deckDisplayConfigs';
import {
  deckDisplayFromConfig,
  renderDecksFromConfig,
} from './deckDisplayHelpers';
import { addNotesTexts } from './notesTexts';
import type { UnitInfoTextInterface } from './types';
import {
  addValue,
  decksDisplay,
  description,
  inclination,
  loadDisplay,
  modelName,
  nbrOfDecks,
  unitName,
  wetScreening,
} from './utils';

export const getScreenTexts = (
  unit: UnitInterface,
  simulationResult: SimulationResultInterface | undefined,
): UnitInfoTextInterface[] => {
  const textArray: UnitInfoTextInterface[] = [];
  const unitInfo = unit.unitInfo as UnitInfoCMScreenInterface;
  const simunitInfo =
    simulationResult?.simulationUnitInfo as SimulationResponseUnitInfoInterface;
  const load = simulationResult?.simulationUnitInfo?.load;

  unitName(unit.type, unitInfo, textArray);
  modelName(unitInfo, textArray);
  nbrOfDecks(unitInfo, textArray);
  wetScreening(unitInfo, textArray);
  inclination(unitInfo, textArray);
  decksDisplay(unit.type, unitInfo, simunitInfo, textArray);
  loadDisplay(unitInfo, load, textArray);
  addNotesTexts({ unitInfo, textArray });

  return textArray;
};

export const getLinearMotionScreenTexts = (
  unit: UnitInterface,
  simulationResult: SimulationResultInterface | undefined,
): UnitInfoTextInterface[] => {
  const textArray: UnitInfoTextInterface[] = [];
  const unitInfo = unit.unitInfo as UnitInfoLMScreenInterface;
  const simunitInfo =
    simulationResult?.simulationUnitInfo as SimulationResponseUnitInfoInterface;
  const load = simulationResult?.simulationUnitInfo?.load;

  unitName(unit.type, unitInfo, textArray);
  modelName(unitInfo, textArray);
  nbrOfDecks(unitInfo, textArray);
  wetScreening(unitInfo, textArray);
  inclination(unitInfo, textArray);
  decksDisplay(unit.type, unitInfo, simunitInfo, textArray);
  loadDisplay(unitInfo, load, textArray);
  addNotesTexts({ unitInfo, textArray });

  return textArray;
};

export const getGenericScreenTexts = (
  unit: UnitInterface,
): UnitInfoTextInterface[] => {
  const textArray: UnitInfoTextInterface[] = [];
  const unitInfo = unit.unitInfo as UnitInfoGenericScreenInterface;

  unitName(unit.type, unitInfo, textArray);
  modelName(unitInfo, textArray);
  description(unitInfo, textArray);
  nbrOfDecks(unitInfo, textArray);

  renderDecksFromConfig(
    unitInfo.deckList,
    unitInfo.nbrOfDecks,
    genericScreenDeckConfig.properties,
    textArray,
  );

  addNotesTexts({ unitInfo, textArray });

  return textArray;
};

export const getBananaScreenTexts = (
  unit: UnitInterface,
  simulationResult: SimulationResultInterface | undefined,
): UnitInfoTextInterface[] => {
  const unitInfo = unit.unitInfo as UnitInfoBananaScreenInterface;
  const load = simulationResult?.simulationUnitInfo?.load;
  const textArray: UnitInfoTextInterface[] = [];
  const add = addValue(textArray);

  unitName(unit.type, unitInfo, textArray);
  add(`Decks: ${unitInfo.nbrOfDecks}`, unitInfo.display.displayNbrOfDecks);
  wetScreening(unitInfo, textArray);

  renderDecksFromConfig(
    unitInfo.deckList,
    unitInfo.nbrOfDecks,
    bananaScreenDeckConfig.properties,
    textArray,
  );

  loadDisplay(unitInfo, load, textArray);

  addNotesTexts({ unitInfo, textArray });

  return textArray;
};

export const getMultislopeScreenTexts = (
  unit: UnitInterface,
  simulationResult: SimulationResultInterface | undefined,
): UnitInfoTextInterface[] => {
  const unitInfo = unit.unitInfo as UnitInfoMultislopeScreenInterface;
  const simunitInfo =
    simulationResult?.simulationUnitInfo as SimulationResponseUnitInfoInterface;
  const load = simulationResult?.simulationUnitInfo?.load;
  const textArray: UnitInfoTextInterface[] = [];
  const add = addValue(textArray);

  unitName(unit.type, unitInfo, textArray);
  add(`Decks: ${unitInfo.nbrOfDecks}`, unitInfo.display.displayNumberOfDecks);
  wetScreening(unitInfo, textArray);
  decksDisplay(unit.type, unitInfo, simunitInfo, textArray);
  loadDisplay(unitInfo, load, textArray);

  addNotesTexts({ unitInfo, textArray });

  return textArray;
};

export const getGrizzlyScreenTexts = (
  unit: UnitInterface,
  simulationResult: SimulationResultInterface | undefined,
): UnitInfoTextInterface[] => {
  const unitInfo = unit.unitInfo as UnitInfoGrizzlyScreenSGInterface;
  const load = simulationResult?.simulationUnitInfo?.load;
  const textArray: UnitInfoTextInterface[] = [];

  unitName(unit.type, unitInfo, textArray);
  modelName(unitInfo, textArray);
  nbrOfDecks(unitInfo, textArray);

  Object.values(unitInfo.deckList).forEach((deck: any, index: number) => {
    if (index < unitInfo.nbrOfDecks) {
      const properties = grizzlyScreenDeckConfig.getProperties({
        deckIndex: index,
      });
      deckDisplayFromConfig(
        deck,
        properties,
        index,
        textArray,
        grizzlyScreenDeckNames?.[index],
      );
    }
  });

  loadDisplay(unitInfo, load, textArray);
  addNotesTexts({ unitInfo, textArray });

  return textArray;
};

export const getSchenckScreenTexts = (
  unit: UnitInterface,
  simulationResult: SimulationResultInterface | undefined,
): UnitInfoTextInterface[] => {
  const textArray: UnitInfoTextInterface[] = [];
  const add = addValue(textArray);
  const unitInfo = unit.unitInfo as UnitInfoSchenckScreenInterface;
  const load = simulationResult?.simulationUnitInfo?.load;

  unitName(unit.type, unitInfo, textArray);
  add(unitInfo.modelName, unitInfo.display.displayModelName);
  add(`Decks: ${unitInfo.nbrOfDecks}`, unitInfo.display.displayNbrOfDecks);
  wetScreening(unitInfo, textArray);

  renderDecksFromConfig(
    unitInfo.deckList,
    unitInfo.nbrOfDecks,
    bananaScreenDeckConfig.properties,
    textArray,
  );

  loadDisplay(unitInfo, load, textArray);
  addNotesTexts({ unitInfo, textArray });

  return textArray;
};

export const getKwataniScreenTexts = (
  unit: UnitInterface,
  simulationResult: SimulationResultInterface | undefined,
): UnitInfoTextInterface[] => {
  const textArray: UnitInfoTextInterface[] = [];
  const add = addValue(textArray);
  const unitInfo = unit.unitInfo as UnitInfoKwataniScreenInterface;
  const load = simulationResult?.simulationUnitInfo?.load;

  unitName(unit.type, unitInfo, textArray);
  modelName(unitInfo, textArray);
  add(`Decks: ${unitInfo.nbrOfDecks}`, unitInfo.display.displayNbrOfDecks);
  wetScreening(unitInfo, textArray);

  renderDecksFromConfig(
    unitInfo.deckList,
    unitInfo.nbrOfDecks,
    kwataniScreenDeckConfig.properties,
    textArray,
  );

  loadDisplay(unitInfo, load, textArray);
  addNotesTexts({ unitInfo, textArray });

  return textArray;
};

export const getFreeFallScreenSFTexts = (
  unit: UnitInterface,
  simulationResult: SimulationResultInterface | undefined,
): UnitInfoTextInterface[] => {
  const textArray: UnitInfoTextInterface[] = [];
  const unitInfo = unit.unitInfo as UnitInfoFreeFallScreenSFInterface;
  const load = simulationResult?.simulationUnitInfo?.load;

  unitName(unit.type, unitInfo, textArray);
  modelName(unitInfo, textArray);
  nbrOfDecks(unitInfo, textArray);

  renderDecksFromConfig(
    unitInfo.deckList,
    unitInfo.nbrOfDecks,
    freeFallScreenDeckConfig.properties,
    textArray,
  );

  loadDisplay(unitInfo, load, textArray);
  addNotesTexts({ unitInfo, textArray });

  return textArray;
};

export const getFreeFallScreenSSTexts = (
  unit: UnitInterface,
  simulationResult: SimulationResultInterface | undefined,
): UnitInfoTextInterface[] => {
  const textArray: UnitInfoTextInterface[] = [];
  const load = simulationResult?.simulationUnitInfo?.load;
  const unitInfo = unit.unitInfo as UnitInfoFreeFallScreenSSInterface;

  unitName(unit.type, unitInfo, textArray);
  modelName(unitInfo, textArray);
  nbrOfDecks(unitInfo, textArray);

  renderDecksFromConfig(
    unitInfo.deckList,
    unitInfo.nbrOfDecks,
    freeFallScreenDeckConfig.properties,
    textArray,
  );

  loadDisplay(unitInfo, load, textArray);
  addNotesTexts({ unitInfo, textArray });

  return textArray;
};
